import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  Grid,
  Chip
} from '@mui/material';
import {
  Save,
  Refresh,
  GetApp,
  Lock,
  Link as LinkIcon,
  CheckCircle
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/auth';
import PermissionGuard from './PermissionGuard';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:10000';

const AppDownloadSettings = () => {
  const { token, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [downloadUrl, setDownloadUrl] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');

      if (!isAuthenticated || !token) {
        setError('Please login to manage app download settings');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/app-download/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const settings = response.data;
      setDownloadUrl(settings.downloadUrl || '');
      setAccessCode(settings.accessCode || '');
      setIsEnabled(settings.isEnabled);
      setError('');
    } catch (err) {
      console.error('Error fetching app download settings:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view app download settings');
      } else {
        setError(err.response?.data?.error || 'Failed to fetch app download settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate access code
      if (accessCode && (accessCode.length !== 4 || !/^\d{4}$/.test(accessCode))) {
        setError('Access code must be exactly 4 digits');
        setSaving(false);
        return;
      }

      await axios.put(
        `${API_BASE_URL}/api/app-download/admin/settings`,
        {
          downloadUrl: downloadUrl || null,
          accessCode: accessCode || '0000',
          isEnabled
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('App download settings saved successfully!');
      setError('');
      fetchSettings();
    } catch (err) {
      console.error('Error saving app download settings:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to edit app download settings');
      } else {
        setError(err.response?.data?.error || 'Failed to save app download settings');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <PermissionGuard anyOf={[
      { resource: 'app_download', action: 'view' },
      { resource: 'app_download', action: 'edit' }
    ]}>
      <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            App Download Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Manage the mobile app download feature on the landing page
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Status Card */}
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3, background: isEnabled ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #757575 0%, #9e9e9e 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <CheckCircle sx={{ fontSize: { xs: 32, sm: 40 } }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Download Feature Status
              </Typography>
              <Chip
                label={isEnabled ? 'ENABLED' : 'DISABLED'}
                color={isEnabled ? 'success' : 'default'}
                sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Settings Form */}
        <Card elevation={2}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GetApp color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    Enable/Disable Download Feature
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      onChange={(e) => setIsEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={isEnabled ? "Download feature is enabled" : "Download feature is disabled"}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                  When disabled, users will not see the download option on the landing page
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LinkIcon color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    Download URL
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Google Drive URL or Download Link"
                  value={downloadUrl}
                  onChange={(e) => setDownloadUrl(e.target.value)}
                  placeholder="https://drive.google.com/..."
                  helperText="Enter the URL where users will be redirected to download the app"
                  variant="outlined"
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Lock color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    4-Digit Access Code
                  </Typography>
                </Box>
                <TextField
                  label="Access Code"
                  value={accessCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setAccessCode(value);
                  }}
                  placeholder="0000"
                  helperText="Users must enter this 4-digit code before they can download the app"
                  variant="outlined"
                  inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                  sx={{ maxWidth: 200 }}
                  InputProps={{
                    sx: { fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 'bold', letterSpacing: '0.2em' }
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchSettings}
            disabled={saving}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Reset
          </Button>
          <PermissionGuard resource="app_download" action="edit">
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </PermissionGuard>
        </Box>
      </Container>
    </PermissionGuard>
  );
};

export default AppDownloadSettings;
